import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Pause, RotateCcw, Rewind, FastForward, Loader2, Flame, Eye, MousePointer2 } from "lucide-react";

function heatColor(v) {
  // 0..1 → sky → amber → rose gradient
  if (v < 0.35) return `rgba(56, 189, 248, ${0.15 + v * 0.9})`;
  if (v < 0.7) return `rgba(251, 191, 36, ${0.25 + v * 0.5})`;
  return `rgba(244, 63, 94, ${0.35 + v * 0.5})`;
}

export default function SignalReplay({ signal, open, onOpenChange }) {
  const [replay, setReplay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2); // playback speed multiplier
  const [elapsed, setElapsed] = useState(0); // seconds along the virtual timeline
  const rafRef = useRef(null);
  const lastTick = useRef(null);

  useEffect(() => {
    if (!open || !signal) return;
    setReplay(null);
    setElapsed(0);
    setPlaying(false);
    setLoading(true);
    api.get(`/signals/${signal.signal_id}/replay`)
      .then((r) => {
        setReplay(r.data);
        setPlaying(true);
      })
      .finally(() => setLoading(false));
  }, [open, signal]);

  useEffect(() => {
    const tick = (ts) => {
      if (lastTick.current == null) lastTick.current = ts;
      const dt = (ts - lastTick.current) / 1000;
      lastTick.current = ts;
      setElapsed((e) => {
        const next = e + dt * speed;
        if (replay && next >= replay.total_dwell) {
          setPlaying(false);
          return replay.total_dwell;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    if (playing) {
      lastTick.current = null;
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [playing, speed, replay]);

  const activeEvent = useMemo(() => {
    if (!replay) return null;
    let cur = replay.events[0];
    for (const ev of replay.events) {
      if (elapsed >= ev.t) cur = ev;
      else break;
    }
    return cur;
  }, [replay, elapsed]);

  const progress = replay ? Math.min(100, (elapsed / replay.total_dwell) * 100) : 0;
  const scrollPct = activeEvent?.scroll_pct ?? 0;

  const restart = () => { setElapsed(0); setPlaying(true); };

  if (!signal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl bg-[#0B1020] border-white/10 text-slate-100 p-0 overflow-hidden"
        data-testid="signal-replay-dialog"
      >
        <DialogHeader className="p-6 pb-4 border-b border-white/5">
          <DialogTitle className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-rose-400" />
            Replay · {signal.visitor_name}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Deterministic scroll-and-dwell reconstruction from this visit's intent, page, and revisit signals.
          </DialogDescription>
        </DialogHeader>

        {loading || !replay ? (
          <div className="h-96 grid place-items-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_320px] gap-0">
            {/* Page mockup + scroll indicator */}
            <div className="p-6">
              <div className="relative rounded-xl border border-white/10 bg-black/40 overflow-hidden" style={{ aspectRatio: "9/16", maxHeight: 460 }}>
                {/* Stylized page sections */}
                <div className="absolute inset-0">
                  {replay.sections.map((s) => {
                    const heat = replay.heatmap.find((h) => h.id === s.id);
                    const bg = heat ? heatColor(heat.heat) : "rgba(255,255,255,0.02)";
                    return (
                      <div
                        key={s.id}
                        className="absolute left-0 right-0 border-b border-white/5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-white/60 transition-colors"
                        style={{ top: `${s.y_pct}%`, height: `${s.height_pct}%`, background: bg }}
                      >
                        <div className="truncate">{s.name}</div>
                      </div>
                    );
                  })}
                </div>
                {/* Scroll indicator */}
                <motion.div
                  className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-sky-400 to-transparent pointer-events-none"
                  animate={{ top: `${scrollPct}%` }}
                  transition={{ type: "spring", stiffness: 40, damping: 15 }}
                  data-testid="replay-scroll-marker"
                >
                  <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-sky-500 text-slate-950 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                    {Math.round(scrollPct)}%
                  </div>
                </motion.div>
                <AnimatePresence>
                  {activeEvent?.event === "revisit" && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute right-2 top-2 bg-rose-500/20 border border-rose-400/40 text-rose-200 text-[10px] font-mono px-2 py-1 rounded-full flex items-center gap-1"
                    >
                      <RotateCcw className="w-2.5 h-2.5" /> Revisited
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Playback controls */}
              <div className="mt-4 flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={restart} data-testid="replay-restart-btn" className="border-white/10 bg-white/[0.02] h-8">
                  <Rewind className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => (elapsed >= replay.total_dwell ? restart() : setPlaying((p) => !p))}
                  data-testid="replay-playpause-btn"
                  className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold h-8"
                >
                  {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSpeed((s) => (s === 8 ? 1 : s * 2))}
                  data-testid="replay-speed-btn"
                  className="border-white/10 bg-white/[0.02] h-8 font-mono text-xs"
                >
                  <FastForward className="w-3 h-3 mr-1" /> {speed}×
                </Button>
                <div className="flex-1 relative h-2 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-sky-500 to-emerald-400" style={{ width: `${progress}%` }} data-testid="replay-progress-bar" />
                </div>
                <div className="text-xs font-mono text-slate-400 w-16 text-right">
                  {Math.round(elapsed)}s / {replay.total_dwell}s
                </div>
              </div>

              {activeEvent && (
                <div className="mt-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs text-slate-300 flex items-center gap-2" data-testid="replay-current-event">
                  {activeEvent.event === "revisit" ? <RotateCcw className="w-3.5 h-3.5 text-rose-300" /> : <Eye className="w-3.5 h-3.5 text-sky-300" />}
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">t+{activeEvent.t}s ·</span>
                  <span className="truncate">{activeEvent.section_name}</span>
                  {activeEvent.detail && <span className="text-slate-500 truncate">— {activeEvent.detail}</span>}
                </div>
              )}
            </div>

            {/* Heatmap sidebar */}
            <div className="border-l border-white/5 bg-black/20 p-6 max-h-[560px] overflow-y-auto">
              <div className="text-[10px] uppercase font-mono tracking-widest text-emerald-300 mb-3 flex items-center gap-1">
                <MousePointer2 className="w-3 h-3" /> Dwell heatmap
              </div>
              <div className="space-y-2">
                {replay.heatmap.map((h) => (
                  <div key={h.id} className="text-xs" data-testid={`replay-heat-${h.id}`}>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <span className="truncate text-slate-300">{h.name}</span>
                      <span className="font-mono text-slate-500 shrink-0">{h.dwell_s}s</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${Math.max(6, h.heat * 100)}%`, background: heatColor(h.heat) }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-white/5">
                <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-2">Verdict</div>
                {(() => {
                  const hotSection = [...replay.heatmap].sort((a, b) => b.dwell_s - a.dwell_s)[0];
                  return (
                    <div className="text-xs text-slate-300 leading-relaxed">
                      Spent the most time on <strong className="text-white">{hotSection.name}</strong> ({hotSection.dwell_s}s).
                      This is what caught <strong className="text-white">{signal.visitor_first || signal.visitor_name?.split(" ")[0]}</strong>'s eye — lead with it in your reply.
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
